import axios from 'axios'

const runtime_state = {
	initialized: false,
	bootstrap_api_url: null,
	api_base_url: null,
	front_end_base_url: null,
	convention_host: null,
	requested_base_domain: null,
	bootstrap_response: null,
	bootstrap_error: null,
}

function normalizeBaseUrl(candidate) {
	const str = (candidate || '').toString().trim()
	if (!str) return null
	return str.replace(/\/+$/, '')
}

// Pre-bootstrap fallback: assumes last two labels are the base domain. This is wrong for public-suffix TLDs like *.co.uk, but acceptable as a temporary guess before the backend provides the correct registrable domain.
function preliminaryBaseDomain(hostname) {
	const host = (hostname || '').toString().trim().toLowerCase()
	if (!host) return null

	const parts = host.split('.').filter(Boolean)
	if (parts.length <= 2) return host
	return parts.slice(-2).join('.')
}

function normalizeBaseDomain(candidate) {
	const str = (candidate || '').toString().trim().toLowerCase()
	return str || null
}

function isWithinBaseDomain({ hostname, base_domain }) {
	const host = (hostname || '').toString().trim().toLowerCase()
	const base = (base_domain || '').toString().trim().toLowerCase()
	if (!host || !base) return false

	return host === base || host.endsWith(`.${base}`)
}

function isLocalhostDomain(base_domain) {
	const base = (base_domain || '').toString().trim().toLowerCase()
	if (!base) return false

	if (base === 'localhost') return true
	if (base.endsWith('.localhost')) return true
	if (base.match(/^\d{1,3}(?:\.\d{1,3}){3}$/)) return true

	return false
}

function deriveFallbackApiBaseUrl({ convention_host, requested_base_domain }) {
	const host = (convention_host || '').toString().trim().toLowerCase()
	if (!host) return null

	if (host.startsWith('tickets.')) {
		return `https://${host.replace(/^tickets\./, 'tickets-api.')}`
	}

	if (host.startsWith('app.')) {
		return `https://${host.replace(/^app\./, 'api.')}`
	}

	// Prefer backend-provided registrable domain (public-suffix aware).
	// Fall back to preliminary guess only when bootstrap hasn't responded yet.
	const base_domain = requested_base_domain || preliminaryBaseDomain(host)
	return base_domain ? `https://api.${base_domain}` : null
}

function isSafeApiBaseUrl({ api_base_url, requested_base_domain }) {
	if (!api_base_url) return false

	let url
	try {
		url = new URL(api_base_url)
	} catch {
		return false
	}

	if (url.protocol !== 'https:') return false
	if (!url.hostname) return false
	// Permissive fallback: if we don't know the expected base domain, we can't reject the URL.
	if (!requested_base_domain) return true

	// Basic safety: require the API host to be within the same registrable base domain (dot-boundary).
	return isWithinBaseDomain({ hostname: url.hostname, base_domain: requested_base_domain })
}

async function fetchBootstrap({ bootstrap_api_url, convention_host }) {
	const base = normalizeBaseUrl(bootstrap_api_url)
	if (!base) {
		throw new Error('Missing bootstrap API URL (process.env.API_URL)')
	}

	const url = `${base}/routing/bootstrap`

	const response = await axios({
		method: 'GET',
		url,
		params: {
			host: convention_host,
		},
		withCredentials: false,
		headers: {
			'X-Convention-Host': convention_host,
			'X-Front-End-Build-Number': process.env.BUILD_NUMBER,
		},
	})

	return response.data
}

function configureAxiosDefaults({ api_base_url, convention_host }) {
	axios.defaults.baseURL = normalizeBaseUrl(api_base_url)
	axios.defaults.withCredentials = true
	axios.defaults.xsrfCookieName = 'X-CSRF-Token'
	axios.defaults.xsrfHeaderName = 'X-CSRF-Token'
	axios.defaults.headers.common['X-Front-End-Build-Number'] = process.env.BUILD_NUMBER

	if (typeof window !== 'undefined' && convention_host) {
		axios.defaults.headers.common['X-Convention-Host'] = convention_host
	}
}

async function warmCsrfCookie() {
	// Any unauthenticated GET that runs ApplicationController#set_csrf_cookie is fine.
	await axios({
		method: 'GET',
		url: '/health',
	})
}

function isStrictRuntimeEnv(runtime_env) {
	const env = (runtime_env || '').toString().trim().toLowerCase()
	return env === 'stage' || env === 'production'
}

async function initializeRuntime() {
	if (runtime_state.initialized) return runtime_state

	const runtime_env = (process.env.ENV || '').toString().trim()
	const strict_env = isStrictRuntimeEnv(runtime_env)

	const convention_host = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : null

	runtime_state.convention_host = convention_host
	runtime_state.front_end_base_url =
		typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null
	// Pre-bootstrap guess; will be overwritten by backend-provided registrable domain once bootstrap responds.
	runtime_state.requested_base_domain = preliminaryBaseDomain(convention_host)
	runtime_state.bootstrap_api_url = normalizeBaseUrl(process.env.API_URL)

	if (strict_env && !runtime_state.bootstrap_api_url) {
		const err = new Error(`Missing API_URL for env=${runtime_env || 'unknown'}`)
		runtime_state.bootstrap_error = err
		throw err
	}

	let bootstrap_data = null
	try {
		bootstrap_data = await fetchBootstrap({
			bootstrap_api_url: runtime_state.bootstrap_api_url,
			convention_host,
		})
		runtime_state.bootstrap_response = bootstrap_data
	} catch (err) {
		runtime_state.bootstrap_error = err
	}

	const safe_base_domain = normalizeBaseDomain(bootstrap_data?.requested_base_domain)
	if (safe_base_domain) runtime_state.requested_base_domain = safe_base_domain

	const is_localhost_domain = isLocalhostDomain(runtime_state.requested_base_domain)
	const bootstrap_succeeded = Boolean(bootstrap_data)

	let api_base_url = normalizeBaseUrl(bootstrap_data?.convention_series?.backend_base_url)

	// Local dev uses explicit ports (e.g. https://localhost:4000). Avoid switching to a
	// canonical host like https://api.localhost unless the developer explicitly opts into it.
	if (is_localhost_domain) {
		api_base_url = runtime_state.bootstrap_api_url
	} else if (bootstrap_succeeded) {
		// Only apply fallback mapping if we successfully bootstrapped but the returned API URL is unsafe.
		if (!isSafeApiBaseUrl({ api_base_url, requested_base_domain: runtime_state.requested_base_domain })) {
			api_base_url = deriveFallbackApiBaseUrl({
				convention_host,
				requested_base_domain: runtime_state.requested_base_domain,
			})
		}
	} else {
		// If bootstrap failed (backend down / wrong API_URL), stick with the configured bootstrap API.
		api_base_url = runtime_state.bootstrap_api_url
	}

	// Absolute last resort: keep using the bootstrap API URL.
	api_base_url = normalizeBaseUrl(api_base_url) || runtime_state.bootstrap_api_url

	runtime_state.api_base_url = api_base_url

	configureAxiosDefaults({ api_base_url, convention_host })

	// Ensure the API host sets its cookies for this base domain before any login POST.
	try {
		await warmCsrfCookie()
	} catch {
		// If /health is misconfigured, we still want the app to render; login will surface the error.
	}

	runtime_state.initialized = true

	if (typeof window !== 'undefined') {
		window.portal_runtime = runtime_state
	}

	return runtime_state
}

function getApiBaseUrl() {
	return normalizeBaseUrl(runtime_state.api_base_url) || normalizeBaseUrl(process.env.API_URL) || ''
}

function getFrontEndBaseUrl() {
	if (runtime_state.front_end_base_url) return runtime_state.front_end_base_url
	if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
	return ''
}

function getConventionHost() {
	return runtime_state.convention_host || (typeof window !== 'undefined' ? window.location?.hostname : null)
}

export {
	initializeRuntime,
	getApiBaseUrl,
	getFrontEndBaseUrl,
	getConventionHost,
	isWithinBaseDomain,
	isLocalhostDomain,
	preliminaryBaseDomain,
	deriveFallbackApiBaseUrl,
	isSafeApiBaseUrl,
}
